import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Search, RotateCcw } from "lucide-react";
import { fmtDate } from "@/lib/format";

export const GROUPS = [
  { key: "letter", label: "Brieven", color: "bg-blue-600" },
  { key: "telefoon", label: "Telefoongesprekken", color: "bg-teal-600" },
  { key: "email", label: "E-mails", color: "bg-violet-600" },
  { key: "notitie", label: "Notities", color: "bg-slate-500" },
  { key: "huisbezoek", label: "Huisbezoeken", color: "bg-amber-600" },
  { key: "sms", label: "SMS", color: "bg-cyan-600" },
  { key: "complaint", label: "Klachten", color: "bg-red-600" },
  { key: "payment", label: "Betalingen", color: "bg-green-600" },
  { key: "payout", label: "Uitbetalingen", color: "bg-yellow-600" },
  { key: "document", label: "Geüploade documenten", color: "bg-[#0F1E3D]" },
];
export const groupOf = (e) => (e.kind === "call" ? e.type : e.kind);

export const DossierItemPicker = ({ items, checked, setChecked }) => {
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const vis = items.filter((e) => !ql || `${e.title} ${e.description || ""}`.toLowerCase().includes(ql));
  const setMany = (list, v) => setChecked((s) => { const n = { ...s }; list.forEach((e) => { n[e.id] = v; }); return n; });
  const total = items.filter((e) => checked[e.id]).length;
  const groups = [...GROUPS, ...[...new Set(items.map(groupOf))].filter((k) => !GROUPS.some((g) => g.key === k)).map((k) => ({ key: k, label: k, color: "bg-slate-400" }))];

  return (
    <div className="space-y-3 min-w-0" data-testid="dossier-picker">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek in berichten…" className="pl-8" data-testid="dossier-picker-search" />
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" className="text-xs px-3 py-2 rounded-full bg-[#0F1E3D] text-white hover:bg-[#1B2D52]" onClick={() => setMany(vis, true)} data-testid="dossier-picker-all">Alles selecteren</button>
          <button type="button" className="text-xs px-3 py-2 rounded-full border hover:bg-slate-50" onClick={() => setMany(vis, false)} data-testid="dossier-picker-none">Niets selecteren</button>
        </div>
      </div>
      <p className="text-xs text-slate-500" data-testid="dossier-picker-count"><b className="text-slate-800">{total}</b> van {items.length} items geselecteerd</p>
      {items.length === 0 && <p className="text-sm text-slate-400 border rounded-lg p-4 text-center">Geen items binnen de gekozen onderdelen, reikwijdte en periode.</p>}
      {groups.map((g) => {
        const list = vis.filter((e) => groupOf(e) === g.key);
        if (!list.length) return null;
        const n = list.filter((e) => checked[e.id]).length;
        const all = n === list.length;
        return (
          <div key={g.key} className="border rounded-xl overflow-hidden" data-testid={`dossier-group-${g.key}`}>
            <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b cursor-pointer">
              <Checkbox checked={all ? true : n ? "indeterminate" : false} onCheckedChange={() => setMany(list, !all)} data-testid={`dossier-group-toggle-${g.key}`} />
              <span className={`h-2 w-2 rounded-full ${g.color}`} />
              <span className="text-sm font-semibold text-slate-700 flex-1">{g.label}</span>
              <span className="text-xs text-slate-500">{n}/{list.length}</span>
            </label>
            <div className="max-h-52 overflow-y-auto divide-y">
              {list.map((e) => (
                <label key={e.id} className="flex items-start gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-amber-50/40">
                  <Checkbox checked={!!checked[e.id]} onCheckedChange={() => setChecked((s) => ({ ...s, [e.id]: !s[e.id] }))} data-testid={`dossier-tlitem-${e.id}`} className="mt-0.5" />
                  <span className="flex-1 min-w-0"><span className="text-slate-400">{fmtDate(e.date)}</span> · <span className="font-medium text-slate-700">{e.title}</span>{e.description ? <span className="block text-slate-500 truncate">{e.description}</span> : null}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const DossierTextEditor = ({ items, texts, setTexts }) => {
  const val = (e, f) => (texts[e.id]?.[f] ?? e[f] ?? "");
  const set = (e, f, v) => setTexts((s) => ({ ...s, [e.id]: { title: val(e, "title"), description: val(e, "description"), ...s[e.id], [f]: v } }));
  const reset = (e) => setTexts((s) => { const n = { ...s }; delete n[e.id]; return n; });
  if (!items.length) return <p className="text-sm text-slate-400">Geen tijdslijn-items geselecteerd.</p>;
  return (
    <div className="space-y-3 min-w-0" data-testid="dossier-text-editor">
      {items.map((e) => {
        const g = GROUPS.find((x) => x.key === groupOf(e));
        return (
          <div key={e.id} className="border rounded-xl p-3 border-l-4" style={{ borderLeftColor: "#C9A227" }} data-testid={`dossier-text-item-${e.id}`}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-[11px] text-slate-500">{fmtDate(e.date)} · <span className={`text-white px-1.5 py-0.5 rounded ${g?.color || "bg-slate-400"}`}>{g?.label || groupOf(e)}</span></span>
              {texts[e.id] && <button type="button" onClick={() => reset(e)} className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid={`dossier-text-reset-${e.id}`}><RotateCcw className="h-3 w-3" /> Origineel</button>}
            </div>
            <Input value={val(e, "title")} onChange={(ev) => set(e, "title", ev.target.value)} className="font-medium mb-1.5" data-testid={`dossier-text-title-${e.id}`} />
            <Textarea value={val(e, "description")} onChange={(ev) => set(e, "description", ev.target.value)} rows={3} className="text-sm" data-testid={`dossier-text-desc-${e.id}`} />
          </div>
        );
      })}
    </div>
  );
};
